import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail, resetSessionExpiredFlag } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=unauth, obj=auth

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return false;
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  useEffect(() => {
    if (!user || user.role !== "client") return;
    const ping = () => api.post("/presence/heartbeat").catch(() => {});
    ping();
    const interval = setInterval(ping, 60000);
    return () => clearInterval(interval);
  }, [user]);

  const login = async (payload) => {
    try {
      const { data } = await api.post("/auth/login", payload);
      resetSessionExpiredFlag();
      setUser(data);
      return { success: true, user: data };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setUser(false);
    }
  };

  const updateProfile = async (display_name) => {
    try {
      await api.patch("/auth/profile", { display_name });
      await fetchMe();
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const changePassword = async (old_password, new_password) => {
    try {
      await api.post("/auth/change-password", { old_password, new_password });
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, updateProfile, changePassword, refreshUser: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
